import * as React from 'react'
import { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { cn } from 'lib/utils'

interface SheetProps {
	open: boolean
	onClose: () => void
	side?: 'left' | 'right'
	children: React.ReactNode
	className?: string
}

const Sheet = ({ open, onClose, side = 'left', children, className }: SheetProps) => {
	const [mounted, setMounted] = useState(false)
	const [visible, setVisible] = useState(false)

	const handleEscape = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		},
		[onClose],
	)

	useEffect(() => {
		if (open) {
			setMounted(true)
			document.addEventListener('keydown', handleEscape)
			document.body.style.overflow = 'hidden'
			// Trigger enter animation on next frame
			requestAnimationFrame(() => {
				requestAnimationFrame(() => setVisible(true))
			})
		} else {
			setVisible(false)
			document.removeEventListener('keydown', handleEscape)
			// Wait for exit animation to finish before unmounting
			const timer = setTimeout(() => {
				setMounted(false)
				document.body.style.overflow = ''
			}, 250)
			return () => clearTimeout(timer)
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [open, handleEscape])

	if (!mounted) return null

	const isLeft = side === 'left'

	return ReactDOM.createPortal(
		<div
			role="dialog"
			aria-modal="true"
			className="fixed inset-0 z-[10001]"
		>
			{/* Backdrop */}
			<div
				className={cn(
					'absolute inset-0 bg-black/50 transition-opacity duration-250',
					visible ? 'opacity-100' : 'opacity-0',
				)}
				onClick={onClose}
			/>

			{/* Panel */}
			<div
				className={cn(
					'absolute top-0 bottom-0 w-[280px] bg-white shadow-xl overflow-y-auto flex flex-col',
					'transition-transform duration-250 ease-out',
					// Only the edge facing the page is rounded; the other sits flush
					// against the screen edge (MYD-529).
					isLeft ? 'left-0 rounded-r-2xl' : 'right-0 rounded-l-2xl',
					visible
						? 'translate-x-0'
						: isLeft
							? '-translate-x-full'
							: 'translate-x-full',
					className,
				)}
			>
				{children}
			</div>
		</div>,
		document.body,
	)
}
Sheet.displayName = 'Sheet'

export { Sheet }
export type { SheetProps }
